import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiClient } from "../modules/close/api/client.js";
import { loadShellEnv } from "../env.js";

/**
 * Platform sign-out (auth.md §5):
 *  - Best-effort revokes the backend refresh-cookie session via POST (`/auth/signout`).
 *  - Calls Okta's federated sign-out (`signOut()` → Okta logout endpoint).
 *  - Clears the in-memory Okta tokens after Okta has had a chance to read the ID token.
 *
 * The signout endpoint lives on the close API, so this reuses the close module's `ApiClient` built
 * from the shell's close API base. One sign-out ends the whole platform session (one document, one
 * token manager), so there is no per-module sign-out.
 */
export interface SignOutProps {
  readonly apiClient?: {
    post: (path: string) => Promise<void>;
  };
}

const SIGN_OUT_PATH = "/auth/signout";

function createSignOutApiClient(): ApiClient {
  return new ApiClient({ baseUrl: loadShellEnv().closeApiBaseUrl });
}

export function SignOut({ apiClient }: SignOutProps): JSX.Element {
  const { oktaAuth } = useOktaAuth();
  const signOutApiClient = useMemo(() => apiClient ?? createSignOutApiClient(), [apiClient]);
  const hasStartedSignOut = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hasStartedSignOut.current) {
      return;
    }
    hasStartedSignOut.current = true;

    async function run(): Promise<void> {
      try {
        await signOutApiClient.post<void>(SIGN_OUT_PATH).catch(() => undefined);
        await oktaAuth.signOut({ postLogoutRedirectUri: window.location.origin });
        oktaAuth.tokenManager.clear();
      } catch {
        oktaAuth.tokenManager.clear();
        setError("Sign-out could not complete. Close this tab to end your session.");
      }
    }
    void run();
  }, [oktaAuth, signOutApiClient]);

  return (
    <section className="mx-auto max-w-md p-8 text-center">
      <h1 className="text-xl font-semibold">Signing you out…</h1>
      {error === null ? (
        <p className="mt-2 text-aire-muted">Clearing your session and redirecting to Okta.</p>
      ) : (
        <p className="mt-2 text-aire-danger" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
